'use client';

/**
 * ObeyaCostPlanLinesCard — cost-plan pivot table: search + Group by
 * (Line / Slot / Vendor), fully server-computed via the `costplan_pivot`
 * strategy (`build_costplan_pivot`). Mode1 canvas's full plan editor —
 * qty / price / spec editing, expand-to-leaf-placement rows, and room
 * assignment all live here.
 *
 * One flat source at the finest grain any dimension needs (one row per
 * line×room_placement, plus an Unassigned row per line for whatever
 * quantity has no placement yet), and every "view" is a `GROUP BY` key
 * over that same array — Line (product_sid) / Slot (slot_sid) / Vendor
 * (vendor_id). Every grouping conserves the plan total by construction,
 * a property of the source array, not of where it's computed.
 *
 * This card used to also serve the Cost Plan rail surface via two
 * opt-in props (`compact`, `interactive`) that trimmed chrome and added
 * a click-to-focus affordance — but neither prop suppressed this card's
 * pre-existing EDITING capability, so the narrow rail mount accidentally
 * became a full plan editor instead of the glance-and-pick summary it
 * was meant to be. Fixed by decomposition, not a flag: the rail now
 * mounts `ObeyaCostPlanRequirementsCard` (`costplan-requirements-card.tsx`),
 * a structurally separate, read-only component that shares this same
 * `costplan_pivot` data source but contains no qty-edit / expand /
 * room-assignment code at all — a component that never implements a
 * capability can't accidentally expose it, which a runtime flag hiding
 * still-present code can't guarantee. This card is mode1-canvas-only
 * again, mounted with zero props.
 *
 * `GroupHeaderRow` and `LeafRow` live in their own files
 * (`group-header-row.tsx`, `leaf-row.tsx`); the room-assign picker and
 * the qty +/- stepper they both use are shared primitives
 * (`room-assign-picker.tsx`, `qty-stepper.tsx`). Pivot payload types
 * live in `costplan-pivot-shared.ts`, shared with the rail's read-only
 * card.
 *
 * Quote column — reference only: a real VendorResponse behind a line's
 * product, never folded into Value. Line mode: every row in the group
 * is the same product, so the group's own quote status is unambiguous.
 * Vendor/Area mode: a group can span several distinct products with
 * different quote status, so the BFF sends a distinct-product count
 * instead of a single price — real per-product detail lives one click
 * away in the leaf rows.
 *
 * @see klyde_edge/klyde_intelligence/projects/handler.py — costplan_pivot
 * @see klyde_edge/klyde_intelligence/projects/queries.py — build_costplan_pivot
 * @see klyde_edge/ui_config/composition/affordances/canvas_chrome.py — select_product / select_vendor_group
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useStrategyData, useMcpLineEdit, useMcpLineRemove } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { usePinnedCostPlanStore } from '@/lib/stores/pinned-costplan-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { useAffordance } from '@/lib/stores/total-matrix-store';
import { GroupHeaderRow } from './group-header-row';
import { LeafRow } from './leaf-row';
import { money } from './costplan-pivot-shared';
import type {
  GroupByKey, PivotGroup, CostPlanPivotData, RoomSlotOption, RoomProgrammeData,
} from './costplan-pivot-shared';

const CELL = 'px-3 py-2';

const GROUP_MODE_AFFORDANCE: Record<GroupByKey, string> = {
  line: 'canvas.costplan_lines.group_by_line',
  slot: 'canvas.costplan_lines.group_by_slot',
  vendor: 'canvas.costplan_lines.group_by_vendor',
};

// Own component: useAffordance is a hook, can't call it inside the
// GROUP_MODES.map() callback below (Rules of Hooks).
function GroupByButton({
  mode, active, onClick, label, first,
}: { mode: GroupByKey; active: boolean; onClick: () => void; label: string; first: boolean }) {
  const testId = useAffordance(GROUP_MODE_AFFORDANCE[mode]);
  return (
    <button
      type="button" data-testid={testId} onClick={onClick}
      className={cn(
        'px-2.5 py-1 text-xs font-medium transition-colors',
        !first && 'border-l border-border',
        active
          ? 'bg-primary/10 text-primary'
          : 'text-muted-foreground hover:bg-muted',
      )}
    >
      {label}
    </button>
  );
}

export function ObeyaCostPlanLinesCard() {
  const viewportSelection = useViewportSelection();
  const { projectId } = viewportSelection;
  // Fallback: page selection > the pinned-costplan-store's live pin —
  // lets this card resolve a plan even under a future zero-prop mount
  // that has no page selection of its own (e.g. a generic matrix-driven
  // LayoutEngine placement). Harmless for mode1's existing canvas usage:
  // viewportSelection.mcpId is always set there, so this link never
  // fires.
  const pinnedMcpId = usePinnedCostPlanStore((s) => s.pinnedMcpId);
  const mcpId = viewportSelection.mcpId ?? pinnedMcpId;
  const { contentKey } = useCardIdentity();
  const strategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'pivot'));
  const editStrategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'line_edit'));
  const removeStrategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'line_remove'));
  const editLine = useMcpLineEdit(editStrategy);
  const removeLine = useMcpLineRemove(removeStrategy);
  const mutatingLines = editLine.isPending || removeLine.isPending;
  const lang = useLanguageStore((s) => s.lang);

  const [groupBy, setGroupBy] = useState<GroupByKey>('line');
  const [queryInput, setQueryInput] = useState('');
  // Debounced — group_by/search are real request params now (BFF does
  // the pivot), so every keystroke would otherwise be a fetch. 300ms
  // matches typical search-debounce convention; group_by itself is NOT
  // debounced (a deliberate click, not a stream of keystrokes).
  const [query, setQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQuery(queryInput), 300);
    return () => clearTimeout(t);
  }, [queryInput]);

  const params = {
    semantic_id: mcpId ?? '',
    group_by: groupBy,
    search: query || undefined,
    ...(lang ? { lang } : {}),
  };
  const { data: raw, isLoading, isFetching } = useStrategyData<CostPlanPivotData>(
    strategy, params, !!mcpId && !!strategy,
  );
  const d = raw?.data;
  const L = d?.labels ?? {};
  const groups = d?.groups ?? [];
  const vendorColumns = d?.vendor_columns ?? [];
  // +1 trailing remove-button column, always present so header/leaf/
  // footer rows line up regardless of group_by mode (only Line mode
  // renders anything into it — see GroupHeaderRow).
  const columnCount = 3 + (vendorColumns.length > 0 ? vendorColumns.length : 1) + 1;

  const editLineHandler = (lineId: string, qty: number) => {
    if (!mcpId) return;
    editLine.mutate({ semantic_id: mcpId, line_id: lineId, quantity: qty });
  };
  const editPriceHandler = (lineId: string, unitPrice: number) => {
    if (!mcpId) return;
    editLine.mutate({ semantic_id: mcpId, line_id: lineId, unit_cost: unitPrice });
  };
  const editSpecHandler = (lineId: string, spec: string) => {
    if (!mcpId) return;
    editLine.mutate({ semantic_id: mcpId, line_id: lineId, spec });
  };
  const editPlacementHandler = (lineId: string, slotSid: string, qty: number) => {
    if (!mcpId) return;
    editLine.mutate({ semantic_id: mcpId, line_id: lineId, room_placement: { slot_sid: slotSid, qty } });
  };
  const removeLineHandler = (lineId: string) => {
    if (!mcpId) return;
    removeLine.mutate({ semantic_id: mcpId, line_id: lineId });
  };

  // Room roster for the "assign to a room" picker on an unassigned leaf
  // row — the one thing a placement leaf row can't do (it only exists
  // once a placement already exists). Self-fetch, not part of the
  // composite costplan_details/pivot payload — same source RoomBoardBody
  // already reads (mcp_room_programme), scoped by project not plan.
  const roomStrategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'room_programme'));
  const { data: roomData } = useStrategyData<RoomProgrammeData>(
    roomStrategy, { project_sid: projectId ?? '' }, !!projectId && !!roomStrategy,
  );
  const roomSlots: RoomSlotOption[] = useMemo(() => [
    ...(roomData?.data?.room_types ?? []),
    ...(roomData?.data?.specific_rooms ?? []),
  ].map((s) => ({ sid: s.sid, name: s.name })), [roomData]);

  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const selectedId = useGraphSelectionStore((s) => s.selectedNode?.id);
  const selectLine = (g: PivotGroup) => {
    const r = g.rows[0];
    if (!r?.product_sid) return;
    // A line's vendor is 1:1 — ride it along so the rail can show both
    // Product and Vendor details without a second click. See
    // graph-selection-store's relatedVendor docstring for why this
    // isn't symmetric (a vendor group has many products, not one).
    const relatedVendor = r.vendor_id
      ? { id: r.vendor_id, label: r.vendor_name ?? 'Vendor', type: 'vendor', properties: {} }
      : null;
    selectNode({ id: r.product_sid, label: r.product_name ?? 'Product', type: 'product', properties: {} }, undefined, relatedVendor);
  };
  const selectVendor = (g: PivotGroup) => {
    const r = g.rows[0];
    if (!r?.vendor_id) return;
    selectNode({ id: r.vendor_id, label: r.vendor_name ?? 'Vendor', type: 'vendor', properties: {} });
  };

  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (key: string) => {
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const queryKey = ['strategy-data', strategy, params] as const;
  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_costplan_lines',
    status: (d ? 'loaded' : mcpId && strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: mcpId && strategy ? queryKey : null,
    text: `Cost plan table: ${groups.length} groups (by ${groupBy})`,
    state: { query, groupBy, open: [...open] },
    json: null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contentKey, d, mcpId, strategy, query, groupBy, open, groups.length]));

  if (!mcpId) {
    return (
      <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
        Select a cost plan
      </div>
    );
  }

  const GROUP_MODES: { key: GroupByKey; label: string }[] = [
    { key: 'line', label: L.group_by_line ?? '' },
    { key: 'slot', label: L.group_by_slot ?? '' },
    { key: 'vendor', label: L.group_by_vendor ?? '' },
  ];

  return (
    // bg-card/border/rounded: same self-applied chrome every sibling
    // obeya card uses — this one never had it, so the search/group-by
    // toolbar (part of THIS card, not a separate placement) sat
    // transparent against the grid's grey background, disconnected
    // from the table below it that only read as white via its sticky
    // <thead>'s own bg-card.
    <div className="h-full flex flex-col overflow-hidden bg-card rounded-xl border border-border">
      <div className="flex items-center gap-2 border-b border-border shrink-0 flex-wrap p-3">
        <div className="flex items-center gap-2 bg-muted/50 border border-border rounded-md px-2.5 py-1.5 flex-1 max-w-sm">
          <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <input
            value={queryInput}
            onChange={(e) => setQueryInput(e.target.value)}
            placeholder={L.search_table_placeholder ?? ''}
            className="bg-transparent text-xs outline-none w-full"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <span className={text.muted}>{L.group_by ?? ''}</span>
          <div className="inline-flex items-center border border-border rounded-md overflow-hidden">
            {GROUP_MODES.map((m, i) => (
              <GroupByButton
                key={m.key}
                mode={m.key}
                active={groupBy === m.key}
                onClick={() => { setGroupBy(m.key); setOpen(new Set()); }}
                label={m.label}
                first={i === 0}
              />
            ))}
          </div>
        </div>
        <span className={cn(text.muted, 'ml-auto')}>{isFetching ? '…' : groups.length}</span>
      </div>
      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-card z-10">
            <tr>
              <th className={cn(CELL, 'border-b border-border text-left', text.eyebrow)}>{L.group_by_line ?? ''}</th>
              <th className={cn(CELL, 'border-b border-border text-right', text.eyebrow)}>{L.col_qty ?? ''}</th>
              <th className={cn(CELL, 'border-b border-border text-right', text.eyebrow)}>{L.col_value ?? ''}</th>
              {vendorColumns.length > 0 ? (
                vendorColumns.map((vc) => (
                  <th key={vc.vendor_sid} className={cn(CELL, 'border-b border-border text-right', text.eyebrow)}>
                    {vc.vendor_name}
                  </th>
                ))
              ) : (
                <th className={cn(CELL, 'border-b border-border text-right', text.eyebrow)}>{L.col_quoted ?? ''}</th>
              )}
              <th className={cn(CELL, 'border-b border-border')} />
            </tr>
          </thead>
          <tbody>
            {isLoading && !d ? (
              <tr><td colSpan={columnCount} className={cn('px-3 py-6 text-center', text.muted)}>…</td></tr>
            ) : groups.length === 0 ? (
              <tr><td colSpan={columnCount} className={cn('px-3 py-6 text-center', text.muted)}>{L.no_table_match ?? ''}</td></tr>
            ) : groups.map((g) => {
              const isOpen = open.has(g.key);
              const isSelected = !!selectedId && (
                (groupBy === 'line' && selectedId === g.rows[0]?.product_sid) ||
                (groupBy === 'vendor' && selectedId === g.rows[0]?.vendor_id)
              );
              // A single-product Line-mode group's own remainder row now
              // renders on the group header (see GroupHeaderRow's
              // `remainder`), not as a leaf — exclude it here so it
              // doesn't render twice. Multi-product groups keep every
              // row as a leaf, each now carrying its own product
              // identity (showProductIdentity below) since there's no
              // single header that could speak for all of them.
              const singleProductLine = groupBy === 'line' && g.product_count <= 1;
              const leaves = singleProductLine ? g.rows.filter((r) => r.slot_sid != null) : g.rows;
              return (
                <Fragment key={g.key}>
                  {g.section_label && (
                    <tr className="bg-muted/40">
                      <td colSpan={columnCount} className={cn(text.muted, 'px-3 py-1.5 font-medium uppercase tracking-wide')}>
                        {g.section_label}
                      </td>
                    </tr>
                  )}
                  <GroupHeaderRow
                    g={g} mode={groupBy} isOpen={isOpen} isSelected={isSelected}
                    onToggle={() => toggle(g.key)}
                    onSelectLine={selectLine} onSelectVendor={selectVendor} L={L}
                    vendorColumns={vendorColumns}
                    onEditQty={editLineHandler} onEditPrice={editPriceHandler} onEditSpec={editSpecHandler}
                    onRemove={removeLineHandler} onEditPlacement={editPlacementHandler}
                    mutating={mutatingLines} roomSlots={roomSlots}
                  />
                  {isOpen && leaves.map((r, i) => (
                    <LeafRow
                      key={`${g.key}-${i}`} r={r} mode={groupBy} L={L} vendorColumns={vendorColumns}
                      onEditPlacement={editPlacementHandler} mutating={mutatingLines} roomSlots={roomSlots}
                      showProductIdentity={groupBy === 'line' && !singleProductLine}
                    />
                  ))}
                </Fragment>
              );
            })}
          </tbody>
          {groups.length > 0 && (
            <tfoot className="sticky bottom-0 bg-card">
              <tr className="border-t border-border">
                <td className={cn(CELL, text.eyebrow)}>{L.col_total ?? ''}</td>
                <td className={CELL} />
                <td className={cn(CELL, 'text-right tabular-nums font-semibold')}>
                  {/* Plan-wide RANGE for Line mode — same reasoning as
                      each group row's own value_low/value_high, rolled
                      up once more. Slot/Vendor mode keeps the plain sum;
                      it's a legitimate total there. */}
                  {groupBy === 'line' ? (
                    (d?.total_value_low ?? 0) === (d?.total_value_high ?? 0)
                      ? money(d?.total_value_low ?? 0)
                      : `${money(d?.total_value_low ?? 0)} – ${money(d?.total_value_high ?? 0)}`
                  ) : money(d?.total_value ?? 0)}
                </td>
                {(vendorColumns.length > 0 ? vendorColumns : [null]).map((vc, i) => (
                  <td key={vc?.vendor_sid ?? i} className={CELL} />
                ))}
                <td className={CELL} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
